/**
 * Privacy-safe sanitization helpers for client log metadata.
 *
 * Free-form user content is summarized as length + hash only; text previews are
 * never included in transported log payloads.
 *
 * @module loggerSanitize
 */

export type LogContext = Record<string, unknown>;

export interface SanitizeLogOptions {
  readonly includeErrorStack?: boolean;
}

const MAX_STRING_LENGTH = 256;
const SENSITIVE_KEY_MARKERS = [
  'password',
  'token',
  'secret',
  'authorization',
  'cookie',
  'credential',
  'jwt',
  'api_key',
  'apikey',
  'email',
];
const FREE_TEXT_KEY_MARKERS = [
  'answer',
  'answer_text',
  'student_answer',
  'essay',
  'prompt',
  'response',
  'transcript',
  'content',
  'audio',
  'audio_data',
  'body',
];

function isSensitiveKey(key: string): boolean {
  const lowered = key.toLowerCase();
  return SENSITIVE_KEY_MARKERS.some((marker) => lowered.includes(marker));
}

function isFreeTextKey(key: string): boolean {
  const lowered = key.toLowerCase();
  return FREE_TEXT_KEY_MARKERS.some((marker) => lowered.includes(marker));
}

function hashString(value: string): string {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash).toString(16);
}

function sha256Prefix(value: string): string {
  try {
    if (typeof process !== 'undefined' && process.versions?.node) {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const { createHash } = require('node:crypto') as typeof import('node:crypto');
      return createHash('sha256').update(value, 'utf8').digest('hex').slice(0, 16);
    }
  } catch {
    // Fall back to a deterministic non-cryptographic hash in browser runtimes.
  }
  return hashString(value).padStart(16, '0').slice(0, 16);
}

function summarizeFreeText(value: string, fieldName?: string): LogContext {
  const summary: LogContext = {
    kind: 'text_summary',
    length: value.length,
    sha256_prefix: sha256Prefix(value),
  };
  if (fieldName) {
    summary.field = fieldName;
  }
  return summary;
}

function summarizeAudioPayload(value: string): LogContext {
  return {
    kind: 'audio_summary',
    length: value.length,
    sha256_prefix: sha256Prefix(value),
  };
}

function sanitizeValue(
  value: unknown,
  fieldName?: string,
  options?: SanitizeLogOptions,
): unknown {
  if (value === null || value === undefined || typeof value === 'boolean' || typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    if (fieldName && isSensitiveKey(fieldName)) {
      return '[REDACTED]';
    }
    if (fieldName && isFreeTextKey(fieldName)) {
      if (fieldName.toLowerCase().includes('audio')) {
        return summarizeAudioPayload(value);
      }
      return summarizeFreeText(value, fieldName);
    }
    if (value.length > MAX_STRING_LENGTH) {
      return summarizeFreeText(value);
    }
    return value;
  }

  if (Array.isArray(value)) {
    return value.slice(0, 50).map((item) => sanitizeValue(item, fieldName, options));
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: summarizeFreeText(value.message, 'message'),
      stack: options?.includeErrorStack ? value.stack : undefined,
    };
  }

  if (typeof value === 'object') {
    return sanitizeLogMetadata(value as LogContext, options);
  }

  return String(value);
}

export function sanitizeLogMetadata(
  context?: LogContext,
  options?: SanitizeLogOptions,
): LogContext | undefined {
  if (!context) {
    return undefined;
  }

  const sanitized: LogContext = {};
  for (const [key, value] of Object.entries(context)) {
    if (value === undefined) {
      continue;
    }
    sanitized[key] = sanitizeValue(value, key, options);
  }
  return Object.keys(sanitized).length > 0 ? sanitized : undefined;
}
